import React, { useState, useEffect } from "react";
import { DownOutlined, CheckOutlined } from "@ant-design/icons";
import { Dropdown, MenuProps } from "antd";

export const ITEM_SIZES = {
  EXTRA_SMALL: "xs",
  SMALL: "s",
  MEDIUM: "m",
  LARGE: "l",
} as const;

export type ItemSizeType = (typeof ITEM_SIZES)[keyof typeof ITEM_SIZES];

const SIZE_STORAGE_KEY = "app_item_size";

const normalizeSize = (val: string | null): ItemSizeType => {
  if (val === "xs" || val === "extra_small") return ITEM_SIZES.EXTRA_SMALL;
  if (val === "s" || val === "small") return ITEM_SIZES.SMALL;
  if (val === "l" || val === "large") return ITEM_SIZES.LARGE;
  return ITEM_SIZES.MEDIUM;
};

export const useItemSize = (defaultSize: ItemSizeType = ITEM_SIZES.MEDIUM) => {
  const [itemSize, setItemSize] = useState<ItemSizeType>(() => {
    try {
      const stored = localStorage.getItem(SIZE_STORAGE_KEY);
      if (stored) {
        return normalizeSize(stored);
      }
    } catch (e) {
      console.error("Error reading item size from localStorage:", e);
    }
    return defaultSize;
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIZE_STORAGE_KEY, itemSize);
    } catch (e) {
      console.error("Error saving item size to localStorage:", e);
    }
  }, [itemSize]);

  return [itemSize, setItemSize] as const;
};

export const getSizeScale = (size?: string): number => {
  switch (size) {
    case ITEM_SIZES.EXTRA_SMALL:
    case "extra_small":
    case "xs":
      return 0.55;
    case ITEM_SIZES.SMALL:
    case "small":
    case "s":
      return 0.74;
    case ITEM_SIZES.LARGE:
    case "large":
    case "l":
      return 1.26;
    case ITEM_SIZES.MEDIUM:
    case "medium":
    case "m":
    default:
      return 1.0;
  }
};

interface SizeSelectProps {
  itemSize: ItemSizeType;
  setItemSize: (size: ItemSizeType) => void;
}

const RectSizeIcon: React.FC<{
  size: ItemSizeType;
  color?: string;
  style?: React.CSSProperties;
}> = ({ size, color = "currentColor", style }) => {
  const normalized = normalizeSize(size);
  const configs = {
    [ITEM_SIZES.EXTRA_SMALL]: { w: 7, h: 5, r: 1 },
    [ITEM_SIZES.SMALL]: { w: 10, h: 7, r: 1.5 },
    [ITEM_SIZES.MEDIUM]: { w: 13, h: 10, r: 2 },
    [ITEM_SIZES.LARGE]: { w: 17, h: 13, r: 2.5 },
  }[normalized] || { w: 13, h: 10, r: 2 };

  return (
    <span
      className="anticon d-inline-flex align-items-center justify-content-center"
      style={{
        width: "18px",
        height: "18px",
        flexShrink: 0,
        ...style,
      }}
    >
      <span
        style={{
          display: "inline-block",
          width: `${configs.w}px`,
          height: `${configs.h}px`,
          border: `1.5px solid ${color}`,
          borderRadius: `${configs.r}px`,
          boxSizing: "border-box",
          transition: "all 0.2s ease",
        }}
      />
    </span>
  );
};

export const SizeSelect: React.FC<SizeSelectProps> = ({ itemSize, setItemSize }) => {
  const currentSize = normalizeSize(itemSize);

  const sizeLabels: Record<string, string> = {
    [ITEM_SIZES.EXTRA_SMALL]: "XS",
    [ITEM_SIZES.SMALL]: "S",
    [ITEM_SIZES.MEDIUM]: "M",
    [ITEM_SIZES.LARGE]: "L",
  };

  const menuItems: MenuProps["items"] = [
    {
      key: ITEM_SIZES.EXTRA_SMALL,
      icon: <RectSizeIcon size={ITEM_SIZES.EXTRA_SMALL} color="#8b5cf6" style={{ marginRight: 8 }} />,
      label: (
        <span className="d-flex align-items-center justify-content-between" style={{ minWidth: 140 }}>
          <span>XS (150px)</span>
          {currentSize === ITEM_SIZES.EXTRA_SMALL && <CheckOutlined style={{ fontSize: 11, color: "#2b3034", marginLeft: 8 }} />}
        </span>
      ),
    },
    {
      key: ITEM_SIZES.SMALL,
      icon: <RectSizeIcon size={ITEM_SIZES.SMALL} color="#3b82f6" style={{ marginRight: 8 }} />,
      label: (
        <span className="d-flex align-items-center justify-content-between" style={{ minWidth: 140 }}>
          <span>S (200px)</span>
          {currentSize === ITEM_SIZES.SMALL && <CheckOutlined style={{ fontSize: 11, color: "#2b3034", marginLeft: 8 }} />}
        </span>
      ),
    },
    {
      key: ITEM_SIZES.MEDIUM,
      icon: <RectSizeIcon size={ITEM_SIZES.MEDIUM} color="#10b981" style={{ marginRight: 8 }} />,
      label: (
        <span className="d-flex align-items-center justify-content-between" style={{ minWidth: 140 }}>
          <span>M (270px)</span>
          {currentSize === ITEM_SIZES.MEDIUM && <CheckOutlined style={{ fontSize: 11, color: "#2b3034", marginLeft: 8 }} />}
        </span>
      ),
    },
    {
      key: ITEM_SIZES.LARGE,
      icon: <RectSizeIcon size={ITEM_SIZES.LARGE} color="#f59e0b" style={{ marginRight: 8 }} />,
      label: (
        <span className="d-flex align-items-center justify-content-between" style={{ minWidth: 140 }}>
          <span>L (340px)</span>
          {currentSize === ITEM_SIZES.LARGE && <CheckOutlined style={{ fontSize: 11, color: "#2b3034", marginLeft: 8 }} />}
        </span>
      ),
    },
  ];

  return (
    <Dropdown
      menu={{
        items: menuItems,
        selectedKeys: [currentSize],
        onClick: ({ key }) => setItemSize(key as ItemSizeType),
      }}
      trigger={["click"]}
      placement="bottom"
    >
      <button
        type="button"
        className="btn d-flex align-items-center gap-2"
        style={{
          backgroundColor: "#ffffff",
          borderColor: "rgba(43, 48, 52, 0.25)",
          borderStyle: "solid",
          borderWidth: "1.5px",
          borderRadius: "50px",
          padding: "7px 16px",
          fontSize: "14px",
          fontWeight: 600,
          color: "#2b3034",
          boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
          transition: "all 0.2s ease",
          cursor: "pointer",
          lineHeight: 1.25,
        }}
      >
        <RectSizeIcon size={currentSize} color="#2b3034" />
        <span>{sizeLabels[currentSize] || "M"}</span>
        <DownOutlined style={{ fontSize: "11px", marginLeft: "2px" }} />
      </button>
    </Dropdown>
  );
};

export default SizeSelect;
